import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, TrendingUp, DollarSign, Package, BarChart3, PieChart } from 'lucide-react';

export default function AdminReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/reports').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  const statCards = [
    { label: 'Total Tenants', value: data?.total_tenants || 0, icon: Users, color: 'text-[#2D6CDF]', bg: 'bg-blue-50' },
    { label: 'Active Subscriptions', value: data?.active_subscriptions || 0, icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Trial', value: data?.trial_subscriptions || 0, icon: Users, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'Expired', value: data?.expired_subscriptions || 0, icon: Users, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Total Revenue', value: `Rp ${fmt(data?.total_revenue)}`, icon: DollarSign, color: 'text-violet-600', bg: 'bg-violet-50' },
    { label: 'MRR', value: `Rp ${fmt(data?.mrr)}`, icon: TrendingUp, color: 'text-teal-600', bg: 'bg-teal-50' },
  ];

  return (
    <div data-testid="admin-reports-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Reports & Analytics</h1>
        <p className="text-sm text-slate-500">Overview performa platform</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map(({ label, value, icon: Icon, color, bg }, i) => (
          <Card key={i} className="border-slate-100 shadow-sm">
            <CardContent className="p-4">
              <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                <Icon size={18} className={color} />
              </div>
              <p className="text-xl font-bold text-slate-900">{value}</p>
              <p className="text-xs text-slate-500 mt-1">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Package Distribution */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PieChart size={18} className="text-[#2D6CDF]" /> Distribusi Paket
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {(data?.package_stats || []).map((pkg, i) => {
                const total = (data?.package_stats || []).reduce((a, p) => a + p.count, 0) || 1;
                const percent = Math.round((pkg.count / total) * 100);
                const colors = ['bg-[#2D6CDF]', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500'];
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-900">{pkg.name}</span>
                      <span className="text-sm text-slate-500">{pkg.count} tenant ({percent}%)</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${colors[i % colors.length]} rounded-full`} style={{ width: `${percent}%` }} />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Rp {fmt(pkg.price)}/bulan</p>
                  </div>
                );
              })}
              {!(data?.package_stats?.length) && <p className="text-center text-slate-400 py-4">Belum ada data</p>}
            </div>
          </CardContent>
        </Card>

        {/* Monthly Signups */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 size={18} className="text-emerald-500" /> Signup Bulanan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end justify-between gap-2 h-48">
              {(data?.monthly_signups || []).map((m, i) => {
                const max = Math.max(...(data?.monthly_signups || []).map(x => x.count), 1);
                const height = (m.count / max) * 100;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center">
                    <div className="w-full flex flex-col items-center justify-end h-40">
                      <span className="text-xs font-medium text-slate-900 mb-1">{m.count}</span>
                      <div className="w-full max-w-8 bg-[#2D6CDF] rounded-t" style={{ height: `${height}%`, minHeight: m.count > 0 ? '8px' : '0' }} />
                    </div>
                    <span className="text-xs text-slate-500 mt-2">{m.month.split(' ')[0]}</span>
                  </div>
                );
              })}
              {!(data?.monthly_signups?.length) && <p className="w-full text-center text-slate-400 py-4">Belum ada data</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Cards */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-[#2D6CDF]/5 to-white">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-2">Conversion Rate</h3>
            <p className="text-3xl font-bold text-[#2D6CDF]">
              {data?.total_tenants ? Math.round((data.active_subscriptions / data.total_tenants) * 100) : 0}%
            </p>
            <p className="text-sm text-slate-500 mt-1">Trial to Paid</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-2">ARPU</h3>
            <p className="text-3xl font-bold text-emerald-600">
              Rp {fmt(data?.active_subscriptions ? Math.round(data.total_revenue / data.active_subscriptions) : 0)}
            </p>
            <p className="text-sm text-slate-500 mt-1">Avg Revenue Per User</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-violet-50 to-white">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-2">Churn Rate</h3>
            <p className="text-3xl font-bold text-violet-600">
              {data?.total_tenants ? Math.round((data.expired_subscriptions / data.total_tenants) * 100) : 0}%
            </p>
            <p className="text-sm text-slate-500 mt-1">Expired subscriptions</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
